
import { useState } from 'react';
import { emergencyContacts, safetyCategories } from '../../../mocks/emergency';

const colorMap: Record<string, { bg: string; text: string; border: string; light: string }> = {
  red: { bg: 'bg-red-600', text: 'text-red-600', border: 'border-red-200', light: 'bg-red-50' },
  teal: { bg: 'bg-teal-600', text: 'text-teal-600', border: 'border-teal-200', light: 'bg-teal-50' },
  orange: { bg: 'bg-orange-500', text: 'text-orange-600', border: 'border-orange-200', light: 'bg-orange-50' },
  amber: { bg: 'bg-amber-500', text: 'text-amber-600', border: 'border-amber-200', light: 'bg-amber-50' },
};

export default function ContactsGrid() {
  const [activeCategory, setActiveCategory] = useState('all');

  const filtered = activeCategory === 'all'
    ? emergencyContacts
    : emergencyContacts.filter((c) => c.category === activeCategory);

  return (
    <section id="contacts" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-10">
          <span className="inline-flex items-center gap-2 text-teal-600 bg-teal-50 text-sm font-medium px-4 py-1.5 rounded-full mb-4">
            <i className="ri-contacts-book-line"></i>
            Important Numbers
          </span>
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Emergency Contacts Directory</h2>
          <p className="text-gray-600 text-sm max-w-xl mx-auto">Quick access to every critical phone number you may need in Roseville and Placer County</p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {safetyCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeCategory === cat.id
                  ? 'bg-teal-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <i className={`${cat.icon} text-base`}></i>
              {cat.label}
            </button>
          ))}
        </div>

        {/* Contacts Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((contact) => {
            const colors = colorMap[contact.color] || colorMap.teal;
            return (
              <div
                key={contact.id}
                className={`relative bg-white rounded-xl border ${colors.border} p-6 hover:shadow-lg transition-all group ${contact.priority ? 'ring-2 ring-offset-1 ring-red-200' : ''}`}
              >
                {contact.priority && (
                  <span className="absolute -top-2.5 right-4 bg-red-600 text-white text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wider">Critical</span>
                )}
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 ${colors.light} rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <i className={`${contact.icon} ${colors.text} text-xl`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-sm mb-1 leading-snug">{contact.name}</h3>
                    <a
                      href={`tel:${contact.phone.replace(/[^0-9]/g, '')}`}
                      className={`text-xl font-bold ${colors.text} hover:underline cursor-pointer block mb-2`}
                    >
                      {contact.phone}
                    </a>
                    <p className="text-gray-500 text-xs leading-relaxed mb-3">{contact.description}</p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {contact.hours}
                      </span>
                      {contact.address && (
                        <span className="flex items-center gap-1">
                          <i className="ri-map-pin-line"></i>
                          <span className="truncate max-w-[180px]">{contact.address}</span>
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                {contact.website && (
                  <a
                    href={contact.website}
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="mt-4 flex items-center justify-center gap-2 w-full py-2 rounded-lg bg-gray-50 hover:bg-gray-100 text-gray-600 text-xs font-medium transition-colors cursor-pointer"
                  >
                    <i className="ri-external-link-line"></i>
                    Visit Website
                  </a>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
